import { goto } from '$app/navigation';
import { ApiService } from '$lib/services/api';
import { PAGES, GTM_EVENT_NAMES } from '$lib/utils/constants';
import { RegistrationPathService } from '$lib/services/registration-path';
import { get } from 'svelte/store';
import { publishFailure, publishSuccess, user, resetStore } from './store';
import { startLoading, stopLoading } from '$lib/stores/app/actions';
import * as Sentry from '@sentry/browser';
import Cookies from 'js-cookie';
// import Logger from '$lib/services/analytics/events';
import { EventType } from '$lib/services/analytics/events/common';
import { AnalyticsService } from '$lib/services/analytics';
import { UnknownError } from '$lib/utils/error';
import { PAYMENT_TYPE } from '@mkt-client/subscription';
import { REG_PATH_STEPS, REG_PATH_KEYS } from '$lib/utils/constants';
import * as UserStore from '../user/store';

/**
 * @param {{ email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect: boolean; logType?: EventType; }} options - options to handle this action
 * @returns {Promise<{data: import('./store').UserStoreState; error?: object}>} result
 */
export async function login(credentialInfo, options = { redirect: false }) {
	try {
		startLoading();
		const loginResp = await ApiService.login({
			username: credentialInfo.email,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType
		});

		const userStatusResp = await ApiService.userGetStatus({
			username: credentialInfo.email,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType
		});

		await AnalyticsService.dataLayerPush({
			eventName: GTM_EVENT_NAMES.LOGIN,
			email: credentialInfo?.email
		});

		const newUserState = {
			...loginResp,
			...userStatusResp,
			password: credentialInfo.password,
			token_valid: true
		};
		publishSuccess(newUserState);

		if (options.logType) logEvent(options.logType, get(user));
		if (options.redirect) {
			const path = await RegistrationPathService.getRedirectPath(get(user));
			// // TODO: make it platform (browser, SSR, in app) agnostic
			await goto(path);
		}

		return { data: get(user), error: null };
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Login Action',
				redirect: options.redirect
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}

/**
 * @param {{ username: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @returns {Promise<object | unknown>} result
 */
export async function checkUserStatusWithCredentials(credentialInfo) {
	try {
		const userStatusResp = await ApiService.userGetStatus({
			username: credentialInfo.username,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType
		});

		const newUserState = { ...userStatusResp };
		publishSuccess(newUserState);
		return get(user);
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: checkUserStatusWithCredentials Action'
			}
		});
		if (!(error instanceof Error)) {
			publishFailure(new Error(error));
		} else {
			publishFailure(error);
		}
	}
}

/**
 * Validates the user.
 * @returns {Promise<object | unknown>} result
 */
export async function validateUser() {
	try {
		const url = new URL(window?.location?.href);
		const token = url.searchParams.get('token');
		if (token) {
			try {
				await ApiService.tokenIntrospect({ token });
			} catch (e) {
				console.error(e);
			}
		}

		const response = await ApiService.validateLogin();
		const newUserState = {
			...response
		};
		publishSuccess(newUserState);
		return get(user);
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: validateUser Action'
			}
		});
		if (!(error instanceof Error)) {
			publishFailure(new Error(error));
		} else {
			publishFailure(error);
		}
	}
}

/**
 * Logs out the user
 * @param {import('$lib/stores/app/types').AppStore} appStore - appStore
 * @returns {Promise<void>} result
 */
export async function logout({ meta }) {
	try {
		startLoading();
		await ApiService.userLogout();
		Object.keys(Cookies.get()).forEach((cookieName) => {
			Cookies.remove(cookieName);
		});
		localStorage.clear();
		resetStore();
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Logout Action'
			}
		});
		console.error(error);
	} finally {
		// TODO: consider removing if use cases arise that dont want to go to login
		await goto(PAGES.INTERNAL.LOGIN);
		stopLoading();
	}
}

/**
 * Submits the users email
 * @param {{ email: string; countryAbbrv: string; usernameType: string; marketingConsent: string; }} registerInfo - credentials
 * @param {{ redirect: boolean }} options - options to handle this action
 * @returns {Promise<object | unknown>} response
 */
export const registerEmail = async (
	registerInfo = {
		email: '',
		countryAbbrv: '',
		usernameType: '',
		marketingConsent: ''
	},
	options = { redirect: false }
) => {
	try {
		// TODO: finish all entries like types and campaign tag.
		const response = await ApiService.prospectRegisterEmail({
			email_address: registerInfo?.email,
			prospect_type: '',
			language_locale: navigator.language,
			campaign_source_tag: '',
			registration_type: registerInfo?.regType,
			subscription_status: registerInfo?.marketingConsent
		});

		// TODO: handle storing email in sessionStorage
		sessionStorage.setItem('tmpEmail', registerInfo.email);
		const newUserState = { tmpEmail: registerInfo.email };
		publishSuccess(newUserState);

		await AnalyticsService.dataLayerPush({
			eventName: GTM_EVENT_NAMES.PROSPECT_REG_SUBMITTED,
			email: registerInfo?.email
		});

		return response;
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: registerEmail Action'
			}
		});
		console.error(error);
	} finally {
		if (options.redirect) {
			goto(PAGES.INTERNAL.SUBSCRIPTION);
		}
	}
};

export const subscriptionUpdatePaymentInfo = async (
	payment,
	subscription_uuid,
	payment_provider
) => {
	try {
		const response = await ApiService.subscriptionUpdatePaymentInfo({
			payment_provider,
			subscription_uuid,
			payment_provider_info: {
				card_name: payment.cardName,
				card_number: payment.cardNumber,
				card_expiration_date: payment.expDate,
				card_cvv: payment.cardCvv,
				card_zip: payment.zipCode
			}
		});
		// Publish? API resp only includes new payment_method: 'paymentech'
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: subscriptionUpdatePaymentInfo Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);
		return { data: get(user), error: er };
	}
};

export const subscriptionOptIn = async (payload) => {
	try {
		const response = await ApiService.subscriptionOptIn(payload);
		console.log('eres ', response);
		// await publishSuccess(response, 'subscription_info');
	} catch (e) {
		console.log(e);
	}
};

export const getUserSubscriptionInfo = async () => {
	try {
		const response = await ApiService.subscriberGetSubscriptionInfo();
		await publishSuccess(response, 'subscription_info');
	} catch (e) {
		console.log(e);
	}
};

export const terminateAccount = async (payload) => {
	try {
		const response = await ApiService.terminate(payload);
	} catch (e) {
		console.log(e);
	}
};

/**
 * @param {EventType} eventType - type of event
 * @param {import('./store').UserStoreState} userInfo - the user state
 * @returns {Promise<object>}
 */
async function logEvent(eventType, userInfo) {
	console.log('log event');
	// return await Logger.log({
	// 	type: eventType,
	// 	deviceId: getDeviceId(),
	// 	meta: get(app).meta,
	// 	aoflProductId: AOFL_PRODUCT_UUIDS.ABCMOUSE,
	// 	detail: {
	// 		firstTimeLogin: userInfo.isFirstLogin,
	// 		userToProductId: userInfo.userToProductUuid
	// 	}
	// });
}

/**
 * @param {{ token: string; email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect: boolean; logType?: EventType; }} options - options to handle this action
 * @returns {Promise<{data: import('./store').UserStoreState; error?: object}>} result
 */
export async function userResetPassword(credentialInfo, options = { redirect: false }) {
	try {
		startLoading();
		const response = await ApiService.userResetPassword(credentialInfo);

		if (options.redirect) await goto(PAGES.INTERNAL.LOGIN);
		return { data: response, error: undefined };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: userResetPassword Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}
